import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Building2, Sparkles, Target, LayoutDashboard, Shield } from 'lucide-react';
import type { WorkflowBlueprint } from '@/hooks/useWorkflowBlueprint';

interface Props {
  blueprint: Partial<WorkflowBlueprint>;
}

const INDUSTRY_LABELS: Record<string, string> = {
  logistics: 'Logistics & Supply Chain',
  ecommerce: 'E-commerce & Retail',
  finance: 'Finance & Banking',
  realestate: 'Real Estate',
  tech: 'Technology / SaaS',
  healthcare: 'Healthcare',
};

const GOAL_LABELS: Record<string, string> = {
  best_selling_products: 'Best-Selling Products',
  highest_revenue_period: 'Highest Revenue Period',
  demand_forecasting: 'Demand Forecasting',
  delivery_performance: 'Delivery Performance',
  customer_churn: 'Customer Churn',
  revenue_growth: 'Revenue Growth',
  lead_conversion: 'Lead Conversion',
  cost_optimization: 'Cost Optimization',
  market_trends: 'Market Trends',
  risk_assessment: 'Risk Assessment',
};

export const SetupReviewStep = ({ blueprint }: Props) => {
  const rules = blueprint.cleaning_rules || {};
  const normalize = rules.normalize || {};
  const exclude = rules.exclude_from_analysis || [];
  const goals = (blueprint.prediction_goals || []) as string[];
  const dash = blueprint.dashboard_config || {};

  const enabledNormRules = Object.entries(normalize).filter(([, v]) => v).map(([k]) => k);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Review Your Configuration</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        The AI will use this blueprint for every workflow run. You can change it anytime from Settings.
      </p>

      {/* Industry */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Industry & Data Source</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{INDUSTRY_LABELS[blueprint.industry || ''] || blueprint.industry}</Badge>
          <Badge variant="outline">{blueprint.data_source_type === 'crm' ? 'CRM Integration' : 'File Upload'}</Badge>
        </div>
      </Card>

      {/* Cleaning */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Data Cleaning Rules</h4>
        </div>
        {enabledNormRules.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {enabledNormRules.map(r => (
              <Badge key={r} variant="secondary" className="text-xs">
                <Check className="h-3 w-3 mr-1" /> {r.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No normalization rules selected</p>
        )}
        {exclude.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mt-1">Excluded: {exclude.join(', ')}</p>
          </div>
        )}
      </Card>

      {/* Predictions */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Prediction Goals ({goals.length})</h4>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {goals.map(g => (
            <Badge key={g} variant="outline" className="text-xs">
              {GOAL_LABELS[g] || g}
            </Badge>
          ))}
        </div>
      </Card>

      {/* Dashboard */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <LayoutDashboard className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Dashboard</h4>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {dash.type && <Badge variant="secondary">{dash.type} dashboard</Badge>}
          {dash.visual_format && <Badge variant="outline">{dash.visual_format}</Badge>}
          {(dash.metrics || []).map((m: string) => (
            <Badge key={m} variant="outline" className="text-xs">{m.replace(/_/g, ' ')}</Badge>
          ))}
        </div>
      </Card>
    </div>
  );
};
